import type { BankConnectionRow, SortDirection } from '@/lib/db-tenant'
import type { FacilityAccountCounts } from '@/lib/facility-account-counts'

/**
 * Pure rules of the facility-scoped connection list: which connections a
 * facility's page lists and what their "Accounts & cards" figure is.
 */

/**
 * The connection as the facility page shows it: `accounts_count` is the
 * facility's own accounts, not the org-wide total.
 *
 * @param row - A row of the org-wide connection list
 * @param counts - The facility's accounts per connection
 */
export function withFacilityAccountCount(
  row: BankConnectionRow,
  counts: FacilityAccountCounts,
): BankConnectionRow {
  return { ...row, accounts_count: counts[row.id] ?? 0 }
}

/**
 * The connections a facility's page lists: only those with at least one
 * account at the facility, each counting only those accounts.
 *
 * The list arrives sorted by the server, whose `accounts_count` sort is by the
 * ORG-WIDE count — so that one column is re-sorted here by the facility's
 * count. `Array.prototype.sort` is stable, so ties keep the server's order.
 *
 * @param rows - The org-wide connection list, in server order
 * @param counts - The facility's accounts per connection
 * @param sort - The active sort, if any
 */
export function scopeConnectionsToFacility(
  rows: readonly BankConnectionRow[],
  counts: FacilityAccountCounts,
  sort: { column?: string; direction?: SortDirection },
): BankConnectionRow[] {
  const scoped = rows
    .filter((row) => (counts[row.id] ?? 0) > 0)
    .map((row) => withFacilityAccountCount(row, counts))
  if (sort.column !== 'accounts_count') return scoped
  const sign = sort.direction === 'desc' ? -1 : 1
  return scoped.sort((a, b) => sign * (a.accounts_count - b.accounts_count))
}
